import { makeStyles, tokens } from "@fluentui/react-components";
import { ArrowCircleUp32Filled, SpinnerIos20Filled } from "@fluentui/react-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import * as React from "react";
import { createQuery } from "@/api/mutations";
import { spinAnimation } from "@/utils/animations";
import { organizationIdAtom, productAtom, requestAtom } from "@/state";
import { getHighlightedText } from "@/utils/editor-actions";
import { getAiirErrorMessage } from "@/utils/error-messages";

export default function QuestionSubmitButton() {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const organizationId = useAtomValue(organizationIdAtom);
  const product = useAtomValue(productAtom);
  const request = useAtomValue(requestAtom);

  const { mutate, isPending } = useMutation({
    mutationFn: createQuery,
    onMutate: () => {
      setErrorMessage(null);
    },
    onSuccess: () => {
      setErrorMessage(null);
      queryClient.invalidateQueries({ queryKey: ["queries"] });
    },
    onError: (error) => {
      setErrorMessage(getAiirErrorMessage(error, "generate the answer"));
    },
  });

  const handleClick = async () => {
    setErrorMessage(null);

    try {
      let selection = await getHighlightedText();
      if (!selection) {
        setErrorMessage("Highlight a question in the workbook first.");
        return;
      }

      selection = selection.trim().replace(/\s+/g, " ").toLowerCase();
      if (!organizationId || !request?.id || !product?.id) {
        setErrorMessage("Select an organization, product, and request before generating an answer.");
        return;
      }

      mutate({ organizationId, productId: product.id, question: selection, requestId: request.id });
    } catch (error: any) {
      setErrorMessage(getAiirErrorMessage(error, "read the highlighted question"));
    }
  };

  return (
    <div className={styles.container}>
      <button className={styles.button} onClick={handleClick} disabled={isPending} aria-label="Generate answer">
        {isPending ? <SpinnerIos20Filled className={styles.loading_spinner} /> : <ArrowCircleUp32Filled />}
      </button>
      {errorMessage && (
        <div className={styles.error} role="alert">
          {errorMessage}
        </div>
      )}
    </div>
  );
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "6px",
  },
  button: {
    border: "none",
    background: "none",
    padding: "0",
    cursor: "pointer",
    color: tokens.colorPaletteLavenderForeground2,
    ["&:hover"]: {
      color: tokens.colorPaletteLavenderBackground2,
    },
  },
  loading_spinner: {
    color: tokens.colorPaletteLavenderForeground2,
    width: "32px",
    height: "32px",
    animationName: spinAnimation,
    animationDuration: "1.5s",
    animationIterationCount: "infinite",
    animationTimingFunction: "linear",
  },
  error: {
    maxWidth: "220px",
    color: tokens.colorPaletteRedForeground1,
    fontSize: "12px",
    lineHeight: "16px",
    textAlign: "right",
  },
});
